import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = {
  success: <CheckCircle size={18} className="text-green-500 flex-shrink-0" />,
  error:   <XCircle    size={18} className="text-red-500   flex-shrink-0" />,
  warning: <AlertTriangle size={18} className="text-yellow-500 flex-shrink-0" />,
  info:    <Info       size={18} className="text-blue-500  flex-shrink-0" />,
};

const COLORS = {
  success: 'border-green-200  bg-green-50  text-green-800',
  error:   'border-red-200    bg-red-50    text-red-800',
  warning: 'border-yellow-200 bg-yellow-50 text-yellow-800',
  info:    'border-blue-200   bg-blue-50   text-blue-800',
};

function ToastItem({ toast, onRemove }) {
  const [out, setOut] = useState(false);

  const dismiss = useCallback(() => {
    setOut(true);
    setTimeout(() => onRemove(toast.id), 220);
  }, [toast.id, onRemove]);

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg max-w-sm w-full pointer-events-auto
        ${COLORS[toast.type]} ${out ? 'toast-out' : 'toast-in'}`}
    >
      {ICONS[toast.type]}
      <p className="flex-1 text-sm font-medium leading-snug">{toast.message}</p>
      <button onClick={dismiss} className="opacity-50 hover:opacity-100 transition-opacity mt-0.5">
        <X size={14} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const show = useCallback((message, type = 'info', duration = 3500) => {
    const id = ++counterRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => {
        const item = prev.find(t => t.id === id);
        if (!item) return prev;
        return prev.map(t => t.id === id ? { ...t, _removing: true } : t);
      });
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 220);
    }, duration);
  }, []);

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (msg, dur) => show(msg, 'success', dur),
    error:   (msg, dur) => show(msg, 'error',   dur || 5000),
    warning: (msg, dur) => show(msg, 'warning', dur),
    info:    (msg, dur) => show(msg, 'info',    dur),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
